import React from "react";
import { Helmet } from "react-helmet-async";

export default function SEO({
  title,
  description,
  canonical,
  ogImage = "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg",
  ogType = "website",
  noindex = false,
  faqs = null,
  schemas = null,
}) {
  // Ensure canonical always uses www.etdanmark.com and no trailing slash (except root)
  let cleanCanonical = canonical;
  if (cleanCanonical) {
    cleanCanonical = cleanCanonical
      .replace(/^https?:\/\/etdanmark\.com/, "https://www.etdanmark.com")
      .replace(/^https?:\/\/www\.etdanmark\.com\/$/, "https://www.etdanmark.com/");
    if (cleanCanonical !== "https://www.etdanmark.com/") {
      cleanCanonical = cleanCanonical.replace(/\/+$/, "");
    }
  }

  // Canonical LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://www.etdanmark.com/#organization",
    name: "ET Danmark ApS",
    url: "https://www.etdanmark.com/",
    logo: "https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=90,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/quick-logo-p-01-0_d4719c06.webp",
    image: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg",
    telephone: "+4542499595",
    email: "info@etdanmark.com",
    vatID: "DK41026138",
    priceRange: "fra 2.500 kr.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Bjerrevej 62 A",
      addressLocality: "Horsens",
      postalCode: "8700",
      addressCountry: "DK",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 55.845,
      longitude: 9.851,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "07:00",
        closes: "17:00",
      },
    ],
    areaServed: [
      {
        "@type": "AdministrativeArea",
        name: "Jylland",
      },
      {
        "@type": "AdministrativeArea",
        name: "Fyn",
      },
    ],
    sameAs: [
      "https://www.facebook.com/profile.php?id=61583288765244",
    ],
  };

  // FAQPage Schema when FAQs are provided
  const faqSchema =
    Array.isArray(faqs) && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q || faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a || faq.answer,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      <html lang="da" />
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {cleanCanonical && <link rel="canonical" href={cleanCanonical} />}

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {cleanCanonical && <meta property="og:url" content={cleanCanonical} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="da_DK" />
      <meta property="og:site_name" content="ET Danmark ApS" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      {Array.isArray(schemas) &&
        schemas.map((s, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(s)}
          </script>
        ))}
    </Helmet>
  );
}
